import React, { useState, useEffect } from 'react';
import {
    Table, Button, Tag, Typography, Card, Space, Modal, Descriptions,
    message, DatePicker, Form, Input, Row, Col, Statistic,
    Divider, Grid, Popconfirm
} from 'antd';
import {
    PlusOutlined, EyeOutlined, CheckCircleOutlined, SendOutlined,
    InboxOutlined, ReloadOutlined, DeleteOutlined, TruckOutlined
} from '@ant-design/icons';
import ExportButton from '../components/ExportButton';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const statusColor = {
    DRAFT: 'default',
    APPROVED: 'blue',
    DISPATCHED: 'orange',
    RECEIVED: 'green',
    CANCELLED: 'red',
};

const fmt = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PalayHaulingPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user, hasPermission } = useAuth();
    const isManagerOrAdmin = ['ADMIN', 'MANAGER'].includes(user?.role);
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [rowLoading, setRowLoading] = useState({});

    // View modal
    const [viewOpen, setViewOpen] = useState(false);
    const [viewOrder, setViewOrder] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    // Receive modal
    const [receiveOpen, setReceiveOpen] = useState(false);
    const [receiveId, setReceiveId] = useState(null);
    const [receiveForm] = Form.useForm();
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => { fetchOrders(); }, [dates]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const params = dates
                ? { start_date: dates[0].format('YYYY-MM-DD'), end_date: dates[1].format('YYYY-MM-DD') }
                : {};
            const { data } = await client.get('/transactions/palay-hauling', { params });
            setOrders(Array.isArray(data) ? data : []);
        } catch {
            message.error('Failed to load palay hauling orders');
        } finally {
            setLoading(false);
        }
    };

    const viewDetail = async (id) => {
        setViewLoading(true);
        setViewOpen(true);
        try {
            const { data } = await client.get(`/transactions/palay-hauling/${id}`);
            setViewOrder(data);
        } catch {
            message.error('Failed to load details');
        } finally {
            setViewLoading(false);
        }
    };

    const setRow = (id, val) => setRowLoading(p => ({ ...p, [id]: val }));

    const handleApprove = async (id) => {
        setRow(id, 'approve');
        try {
            await client.post(`/transactions/palay-hauling/${id}/approve`);
            message.success('Order approved');
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to approve');
        } finally {
            setRow(id, false);
        }
    };

    const handleDispatch = async (id) => {
        setRow(id, 'dispatch');
        try {
            await client.post(`/transactions/palay-hauling/${id}/dispatch`);
            message.success('Order dispatched');
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to dispatch');
        } finally {
            setRow(id, false);
        }
    };

    const openReceive = (id) => {
        setReceiveId(id);
        receiveForm.resetFields();
        setReceiveOpen(true);
    };

    const submitReceive = async (values) => {
        setActionLoading(true);
        try {
            await client.post(`/transactions/palay-hauling/${receiveId}/receive`, values);
            message.success('Palay received at mill');
            setReceiveOpen(false);
            fetchOrders();
            if (viewOpen) {
                const { data } = await client.get(`/transactions/palay-hauling/${receiveId}`);
                setViewOrder(data);
            }
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to receive');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/transactions/palay-hauling/${id}`);
            message.success('Order deleted');
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to delete');
        }
    };

    const handleCancel = async (id) => {
        setRow(id, 'cancel');
        try {
            await client.post(`/transactions/palay-hauling/${id}/cancel`);
            message.success('Hauling order cancelled');
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to cancel');
        } finally {
            setRow(id, false);
        }
    };

    const columns = [
        {
            title: 'Order #',
            dataIndex: 'order_number',
            render: (n, r) => <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => viewDetail(r.id)}>{n}</Button>,
        },
        {
            title: 'From',
            dataIndex: ['source_location', 'name'],
            ellipsis: true,
        },
        {
            title: 'To (Mill)',
            dataIndex: ['destination_location', 'name'],
            ellipsis: true,
        },
        { title: 'Driver', dataIndex: 'driver_name', render: (v) => v || <Text type="secondary">—</Text> },
        {
            title: 'Scheduled',
            dataIndex: 'scheduled_date',
            render: (d) => dayjs(d).format('MMM D, YYYY'),
        },
        {
            title: 'Total Qty (kg)',
            dataIndex: 'total_quantity',
            align: 'right',
            render: (v) => <Text strong>{fmt(v)}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (s) => <Tag color={statusColor[s]}>{s}</Tag>,
        },
        {
            title: 'Actions',
            align: 'right',
            render: (_, r) => (
                <Space size={4}>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => viewDetail(r.id)}>View</Button>
                    {r.status === 'DRAFT' && isManagerOrAdmin && hasPermission('approve_transaction') && (
                        <Button size="small" type="primary" icon={<CheckCircleOutlined />} loading={rowLoading[r.id] === 'approve'} onClick={() => handleApprove(r.id)}>
                            Approve
                        </Button>
                    )}
                    {r.status === 'APPROVED' && (
                        <Button size="small" type="primary" icon={<SendOutlined />} loading={rowLoading[r.id] === 'dispatch'} onClick={() => handleDispatch(r.id)}>
                            Dispatch
                        </Button>
                    )}
                    {r.status === 'DISPATCHED' && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<InboxOutlined />}
                            style={{ background: '#059669', borderColor: '#059669' }}
                            onClick={() => openReceive(r.id)}
                        >
                            Receive at Mill
                        </Button>
                    )}
                    {['DRAFT', 'APPROVED', 'DISPATCHED'].includes(r.status) && isManagerOrAdmin && (
                        <Popconfirm title="Cancel this hauling order?" onConfirm={() => handleCancel(r.id)} okText="Yes" cancelText="No">
                            <Button size="small" danger loading={rowLoading[r.id] === 'cancel'}>Cancel</Button>
                        </Popconfirm>
                    )}
                    {r.status === 'DRAFT' && isManagerOrAdmin && (
                        <Popconfirm title="Delete this order?" onConfirm={() => handleDelete(r.id)} okText="Yes" cancelText="No">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Palay Hauling to Mill</Title>
                    <Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
                        Transport dried palay lots from storage to the milling facility.
                    </Text>
                </div>
                <Space wrap>
                    <RangePicker value={dates} onChange={setDates} style={{ borderRadius: 8 }} />
                    <ExportButton
                        filename="palay-hauling"
                        sheetName="Palay Hauling"
                        title="Palay Hauling to Mill"
                        headers={['Order #', 'From', 'To (Mill)', 'Driver', 'Vehicle', 'Scheduled Date', 'Total Qty (kg)', 'Status']}
                        getData={() => orders.map(o => [
                            o.order_number,
                            o.source_location?.name || '',
                            o.destination_location?.name || '',
                            o.driver_name,
                            o.vehicle?.plate_number || '',
                            dayjs(o.scheduled_date).format('YYYY-MM-DD'),
                            o.total_quantity,
                            o.status,
                        ])}
                        style={{ height: 40, borderRadius: 8 }}
                    />
                    <Button icon={<ReloadOutlined />} onClick={fetchOrders} style={{ height: 40, borderRadius: 8 }} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/palay-hauling/new')} style={{ height: 40, borderRadius: 8, fontWeight: 600 }}>
                        New Hauling Order
                    </Button>
                </Space>
            </div>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    dataSource={orders}
                    columns={columns}
                    loading={loading}
                    rowKey="id"
                    pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                    scroll={{ x: 900 }}
                />
            </Card>

            {/* View Detail Modal */}
            <Modal
                open={viewOpen}
                onCancel={() => setViewOpen(false)}
                title={viewOrder ? `Palay Hauling — ${viewOrder.order_number}` : 'Hauling Order'}
                footer={
                    <Space>
                        {viewOrder?.status === 'DISPATCHED' && (
                            <Button type="primary" icon={<InboxOutlined />} style={{ background: '#059669', borderColor: '#059669' }} onClick={() => { setViewOpen(false); openReceive(viewOrder.id); }}>
                                Receive at Mill
                            </Button>
                        )}
                        <Button onClick={() => setViewOpen(false)}>Close</Button>
                    </Space>
                }
                width={720}
                loading={viewLoading}
            >
                {viewOrder && (
                    <div>
                        <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
                            <Descriptions.Item label="Status"><Tag color={statusColor[viewOrder.status]}>{viewOrder.status}</Tag></Descriptions.Item>
                            <Descriptions.Item label="Total Qty"><Text strong>{fmt(viewOrder.total_quantity)} kg</Text></Descriptions.Item>
                            <Descriptions.Item label="From">{viewOrder.source_location?.name}</Descriptions.Item>
                            <Descriptions.Item label="To (Mill)">{viewOrder.destination_location?.name}</Descriptions.Item>
                            <Descriptions.Item label="Vehicle">{viewOrder.vehicle?.plate_number || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Driver">{viewOrder.driver_name || '—'} {viewOrder.driver_contact ? `· ${viewOrder.driver_contact}` : ''}</Descriptions.Item>
                            <Descriptions.Item label="Scheduled">{dayjs(viewOrder.scheduled_date).format('MMM D, YYYY')}</Descriptions.Item>
                            {viewOrder.dispatched_at && <Descriptions.Item label="Dispatched">{dayjs(viewOrder.dispatched_at).format('MMM D, YYYY HH:mm')}</Descriptions.Item>}
                            {viewOrder.received_at && <Descriptions.Item label="Received at Mill">{dayjs(viewOrder.received_at).format('MMM D, YYYY HH:mm')}</Descriptions.Item>}
                            {viewOrder.remarks && <Descriptions.Item label="Remarks" span={2}>{viewOrder.remarks}</Descriptions.Item>}
                        </Descriptions>

                        <Divider orientation="left" orientationMargin={0}>Palay Lots</Divider>
                        {viewOrder.lots?.map(lot => (
                            <Card key={lot.id} size="small" style={{ marginBottom: 8, borderRadius: 8 }}>
                                <Row gutter={16}>
                                    <Col span={6}><Text strong>{lot.palay_lot?.lot_number}</Text></Col>
                                    <Col span={6}><Text type="secondary">Supplier: </Text><Text>{lot.palay_lot?.purchase?.partner?.name || '—'}</Text></Col>
                                    <Col span={6}><Text type="secondary">Qty: </Text><Text strong>{fmt(lot.quantity)} kg</Text></Col>
                                    <Col span={6}><Text type="secondary">MC: </Text><Text>{lot.moisture_content != null ? `${lot.moisture_content}%` : '—'}</Text></Col>
                                </Row>
                            </Card>
                        ))}
                    </div>
                )}
            </Modal>

            {/* Receive Modal */}
            <Modal
                open={receiveOpen}
                onCancel={() => setReceiveOpen(false)}
                title="Confirm Receipt at Mill"
                footer={null}
                width={480}
            >
                <Form form={receiveForm} onFinish={submitReceive} layout="vertical" style={{ marginTop: 16 }}>
                    <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                        <Text>Confirming receipt will update all lots' location to the destination (mill) and mark them as <Tag color="purple">AT_MILL</Tag> — ready for milling.</Text>
                    </div>
                    <Form.Item name="remarks" label="Receiving Remarks">
                        <Input.TextArea rows={3} placeholder="Any notes on arrival condition..." />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={actionLoading} icon={<InboxOutlined />} style={{ height: 40, borderRadius: 8, background: '#059669', borderColor: '#059669' }}>
                                Confirm Receipt
                            </Button>
                            <Button onClick={() => setReceiveOpen(false)} style={{ height: 40, borderRadius: 8 }}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PalayHaulingPage;
